import React, { useState, useEffect } from 'react';
import { ExternalLink, X, CheckCircle, ShieldCheck, Camera, MessageCircle } from 'lucide-react';
import {
  TELEGRAM_BOT_USERNAME,
  TELEGRAM_CHAT_URL,
  TELEGRAM_ASSISTANT_NAME,
  TELEGRAM_ASSISTANT_TITLE,
} from '../config/telegram';
import { TelegramIcon } from './TelegramAssistantSection';

interface TelegramChatButtonProps {
  variant?: 'floating' | 'inline' | 'banner';
  className?: string;
  customText?: string;
}

export const TelegramChatButton: React.FC<TelegramChatButtonProps> = ({
  variant = 'floating',
  className = '',
  customText,
}) => {
  const [botUsername, setBotUsername] = useState<string>(TELEGRAM_BOT_USERNAME);
  const [telegramUrl, setTelegramUrl] = useState<string>(TELEGRAM_CHAT_URL);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    let isMounted = true;
    fetch('/api/telegram/config')
      .then((res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (isMounted && data && data.success && data.botUsername) {
          setBotUsername(data.botUsername);
          setTelegramUrl(data.telegramUrl || `https://t.me/${data.botUsername}`);
        }
      })
      .catch((err) => {
        console.info('Telegram config sync note:', err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const openTelegram = () => {
    window.open(telegramUrl, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'banner') {
    return (
      <div
        className={`bg-gradient-to-r from-slate-900 via-sky-950 to-slate-900 rounded-3xl p-5 sm:p-6 text-white shadow-lg flex flex-col sm:flex-row items-center justify-between gap-4 border border-sky-800/30 ${className}`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-sky-500/20 border border-sky-400/30 flex items-center justify-center shrink-0 text-sky-400">
            <TelegramIcon className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-sky-400 text-slate-950">
                TELEGRAM AI
              </span>
              <span className="text-xs text-sky-200">Instant LGA Professional Matching</span>
            </div>
            <h3 className="text-base sm:text-lg font-black mt-1">Book Professionals Directly on Telegram</h3>
            <p className="text-xs text-slate-300 mt-0.5">
              Describe your fault, send photos, and receive quotations from verified Nigerian professionals.
            </p>
          </div>
        </div>

        <button
          onClick={openTelegram}
          className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-sky-400 hover:bg-sky-300 text-slate-950 font-black text-xs transition-all flex items-center justify-center gap-2 shadow-md shrink-0 cursor-pointer"
        >
          <TelegramIcon className="w-4 h-4 fill-slate-950" />
          <span>{customText || 'Start Telegram Chat'}</span>
          <ExternalLink className="w-3.5 h-3.5 ml-1 opacity-70" />
        </button>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <button
        onClick={openTelegram}
        className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold transition-all shadow-xs cursor-pointer ${className}`}
      >
        <TelegramIcon className="w-4 h-4" />
        <span>{customText || 'Start Telegram Chat'}</span>
        <ExternalLink className="w-3 h-3 opacity-75" />
      </button>
    );
  }

  // Default: Floating Widget (replaces customer-facing WhatsApp widget)
  return (
    <div className="fixed bottom-[84px] right-3 sm:bottom-6 sm:right-6 z-30 flex flex-col items-end">
      {/* Floating Info Mini-card */}
      {showPopup && (
        <div className="mb-3 w-[calc(100vw-1.5rem)] max-w-xs sm:w-80 bg-white dark:bg-slate-900 rounded-3xl p-4 sm:p-5 shadow-2xl border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 animate-in fade-in slide-in-from-bottom-3 duration-200">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-sky-100 dark:bg-sky-950/80 text-sky-600 dark:text-sky-400 flex items-center justify-center shrink-0">
                <TelegramIcon className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-slate-900 dark:text-white">{TELEGRAM_ASSISTANT_NAME}</h4>
                <span className="text-[10px] text-sky-600 dark:text-sky-400 font-semibold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" />
                  {TELEGRAM_ASSISTANT_TITLE}
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowPopup(false)}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-2 text-xs min-h-[36px] min-w-[36px] flex items-center justify-center cursor-pointer"
              aria-label="Close Telegram Popup"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-xs text-slate-600 dark:text-slate-300 mt-2.5 leading-relaxed">
            Need a plumber, electrician, carpenter, painter, tiler, bricklayer, or other professional?
            Send a message on Telegram and our AI assistant will help diagnose the issue, collect the
            necessary details, and connect you with verified professionals in your LGA.
          </p>

          <div className="mt-3 space-y-1.5 text-[11px] text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-1.5">
              <MessageCircle className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400 shrink-0" />
              <span>Describe your problem directly in Telegram</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400 shrink-0" />
              <span>Send photos or videos of the problem</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400 shrink-0" />
              <span>Verified active pros in your LGA receive your job</span>
            </div>
          </div>

          <button
            onClick={openTelegram}
            className="mt-4 w-full py-3 bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 shadow-md cursor-pointer min-h-[44px]"
          >
            <TelegramIcon className="w-4 h-4 fill-slate-950" />
            <span>Start Telegram Chat</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-70 ml-0.5" />
          </button>
        </div>
      )}

      {/* Floating Button */}
      <div className="flex items-center gap-3">
        {!showPopup && (
          <div
            onClick={() => setShowPopup(true)}
            className="hidden md:flex items-center gap-2 bg-slate-900/90 text-white text-xs py-2 px-3.5 rounded-full shadow-lg border border-sky-500/30 cursor-pointer backdrop-blur-xs hover:bg-slate-900 transition-all animate-bounce"
          >
            <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
            <span className="font-semibold text-sky-200">Need a professional? Chat on Telegram</span>
          </div>
        )}

        <button
          onClick={() => {
            if (showPopup) {
              openTelegram();
            } else {
              setShowPopup(true);
            }
          }}
          aria-label="Chat with JobMatch NG on Telegram"
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-sky-500 hover:bg-sky-400 text-white flex items-center justify-center shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 active:scale-95 cursor-pointer relative group"
        >
          <TelegramIcon className="w-6 h-6 sm:w-7 sm:h-7 fill-white" />
          <span className="absolute top-0 right-0 w-3 h-3 sm:w-3.5 sm:h-3.5 bg-emerald-500 border-2 border-white rounded-full" />
        </button>
      </div>
    </div>
  );
};
